import { useState } from 'react';
import { Button, Checkbox, Flex, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { formatDateTime, formatString } from 'utils/utils';
import KycRfiDocumentInfo from 'components/KycRfi/KycRfiDocumentInfo';
import ParentRfi from 'components/KycRfi/ParentRfi';
import RaiseChildRfiModal from 'components/KycRfi/RaiseChildRfiModal';

const KycRfiCard = ({
  entityId,
  rfi,
  isSubmittedRfi,
  fetchKycDetails,
  documentInfo,
  hasChildRfi,
  rfiTemplates,
  resourceType,
  rfiTagTypes,
  rfiTagSubtypes,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isParentRfiOpen, setIsParentRfiOpen] = useState(false);

  const {
    prompt_format: {
      prompt,
      transaction_id,
      transaction_ids,
      is_document_required,
      is_front_back_required,
      document_type,
      document_sub_type,
      kyc_link,
      rfi_type,
    },
    prompt: { notes, document },
    created_at,
    signed_rfi_document_urls,
    parent_rfi_id,
    rfi_thread,
  } = rfi;
  const transactionIds = [];
  const transactionIdSet = new Set();

  if (transaction_id) {
    transactionIdSet.add(transaction_id);
  }
  if (transaction_ids) {
    for (const transactionId of transaction_ids) {
      transactionIdSet.add(transactionId);
    }
  }
  if (transactionIdSet.size > 0) {
    transactionIds.push(...transactionIdSet);
  }
  const date = new Date(created_at);
  const created_at_date = date.toLocaleDateString();
  const created_at_time = date.toLocaleTimeString();

  const onClose = () => setIsOpen(false);
  const onParentRfiClose = () => setIsParentRfiOpen(false);

  return (
    <>
      <VStack
        spacing={2}
        border={'1px solid'}
        borderColor={'gray.200'}
        p={4}
        borderRadius={'md'}
        background={'white'}
        alignItems={'start'}
        justifyItems={'center'}
        width={'100%'}
      >
        <VStack spacing={1} overflow={'hidden'} align={'start'} justifyItems={'center'} width={'100%'}>
          <HStack>
            {rfi_type?.type && (
              <>
                <Text fontWeight='bold'>RFI Tag Type:</Text>
                <Text>{rfi_type?.type}</Text>
              </>
            )}
            {rfi_type?.sub_type && (
              <>
                <Text fontWeight='bold'>RFI Tag Subtype:</Text>
                <Text>{rfi_type?.sub_type}</Text>
              </>
            )}
          </HStack>
          <HStack alignItems={'center'} justifyContent={'space-between'} width={'100%'}>
            <Text>Question:</Text>
            <HStack gap={2}>
              {isSubmittedRfi && fetchKycDetails && documentInfo && !hasChildRfi && (
                <Button colorScheme='blue' size='sm' onClick={() => setIsOpen(true)}>
                  Raise RFI
                </Button>
              )}
              {parent_rfi_id && rfi_thread?.length > 0 && (
                <Button colorScheme='blue' size='sm' onClick={() => setIsParentRfiOpen(true)}>
                  View Thread
                </Button>
              )}
            </HStack>
          </HStack>
          <Text whiteSpace={'pre-wrap'}>{formatString(prompt)}</Text>
        </VStack>

        <Flex gap={2}>
          <Text>{isSubmittedRfi ? 'Submitted At:' : 'Created At:'}</Text>
          <Text>{`${created_at_date}, ${created_at_time}`}</Text>
        </Flex>

        {transaction_id && (
          <Flex gap={2}>
            <Text>Transaction Id(s):</Text>
            <Text>{transactionIds.join(', ')}</Text>
          </Flex>
        )}

        {document?.map((doc, index) => (
          <KycRfiDocumentInfo document={doc} key={index} url={signed_rfi_document_urls[index]} />
        ))}

        {notes && (
          <Flex gap={2}>
            <Text>Notes:</Text>
            <Text>{notes}</Text>
          </Flex>
        )}

        <Flex gap={4}>
          <Checkbox isChecked={is_document_required} disabled={true}>
            is document required
          </Checkbox>
          <Checkbox isChecked={is_front_back_required} disabled={true}>
            is both-side required
          </Checkbox>
        </Flex>

        <Flex gap={2}>
          <Text>Document Type:</Text>
          <Text wordBreak='break-word'>{document_type}</Text>
        </Flex>
        <Flex gap={2}>
          <Text>Document Subtype:</Text>
          <Text wordBreak='break-word'>
            {Array.isArray(document_sub_type) ? document_sub_type?.join(', ') : document_sub_type || 'N/A'}
          </Text>
        </Flex>
        {kyc_link?.link && (
          <Flex gap={2}>
            <Text>{resourceType} Link:</Text>
            <Link
              href={kyc_link?.link}
              color='blue.500'
              overflow='hidden'
              textOverflow='ellipsis'
              display='block'
              whiteSpace='nowrap'
              isExternal
            >
              {kyc_link?.link}
            </Link>
          </Flex>
        )}
        {kyc_link?.link && kyc_link?.expiry_date && (
          <Flex gap={2}>
            <Text>{resourceType} Link Expiry Date:</Text>
            <Text wordBreak='break-word'>{formatDateTime(kyc_link?.expiry_date)}</Text>
          </Flex>
        )}
      </VStack>
      <RaiseChildRfiModal
        isOpen={isOpen}
        onClose={onClose}
        rfi={rfi}
        entityId={entityId}
        fetchKycDetails={fetchKycDetails}
        documentInfo={documentInfo}
        isSubmittedRfi={isSubmittedRfi}
        rfiTemplates={rfiTemplates}
        rfiTagTypes={rfiTagTypes}
        rfiTagSubtypes={rfiTagSubtypes}
      />
      <ParentRfi
        isOpen={isParentRfiOpen}
        onClose={onParentRfiClose}
        rfiThread={rfi_thread}
        entityId={entityId}
        fetchKycDetails={fetchKycDetails}
        documentInfo={documentInfo}
        isSubmittedRfi={isSubmittedRfi}
        resourceType={resourceType}
      />
    </>
  );
};

export default KycRfiCard;
